import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2,
  CalendarClock,
  PlayCircle,
  CheckCircle2,
  AlertTriangle,
  Copy,
} from "lucide-react";

const JOBS = [
  {
    id: "send-renewal-reminders",
    label: "Renewal Reminders (Annual Plans)",
    description:
      "Finds annual subscribers whose `current_period_end` is 28–32 days out and sends the 30-day Renewal Reminder email. Idempotent via `users.renewal_reminder_sent_for`.",
    endpoint: "POST /api/billing/admin/send-renewal-reminders",
    suggestedCron: "0 9 * * *",
    suggestedCronLabel: "Daily at 9:00 UTC",
  },
];

export default function AdminEmailJobs() {
  const [running, setRunning] = useState(null);
  const [lastRun, setLastRun] = useState({});

  const runJob = async (job) => {
    setRunning(job.id);
    try {
      const path = job.endpoint.split(" ")[1].replace("/api", "");
      const r = await api.post(path);
      setLastRun((prev) => ({
        ...prev,
        [job.id]: { ok: true, result: r.data, at: new Date().toISOString() },
      }));
      const { sent = 0, skipped = 0, errors = 0 } = r.data || {};
      toast.success(`Job done — ${sent} sent, ${skipped} skipped, ${errors} errors`);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Job failed";
      setLastRun((prev) => ({
        ...prev,
        [job.id]: { ok: false, error: detail, at: new Date().toISOString() },
      }));
      toast.error(detail);
    } finally {
      setRunning(null);
    }
  };

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copied");
  };

  return (
    <div className="space-y-4" data-testid="admin-email-jobs">
      {JOBS.map((job) => {
        const last = lastRun[job.id];
        const isRunning = running === job.id;
        return (
          <div key={job.id} className="card p-5" data-testid={`job-${job.id}`}>
            <div className="flex items-start gap-4 flex-wrap">
              <div className="w-10 h-10 rounded-lg bg-[var(--ink)] text-white flex items-center justify-center flex-shrink-0">
                <CalendarClock size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-bold text-base text-[var(--ink)]">{job.label}</h3>
                <p className="text-[var(--ink-2)] text-sm leading-relaxed mt-1">{job.description}</p>

                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                  <div className="bg-[var(--bg)] rounded-md p-3 border border-[var(--line)]">
                    <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] font-semibold mb-1">Endpoint</div>
                    <div className="flex items-center gap-2">
                      <code className="text-xs font-mono text-[var(--ink)] truncate flex-1">{job.endpoint}</code>
                      <button
                        onClick={() => copy(job.endpoint)}
                        className="text-[var(--ink-3)] hover:text-[var(--ink)]"
                        title="Copy"
                      >
                        <Copy size={12} />
                      </button>
                    </div>
                  </div>
                  <div className="bg-[var(--bg)] rounded-md p-3 border border-[var(--line)]">
                    <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] font-semibold mb-1">
                      Suggested cron · {job.suggestedCronLabel}
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="text-xs font-mono text-[var(--ink)] truncate flex-1">{job.suggestedCron}</code>
                      <button
                        onClick={() => copy(job.suggestedCron)}
                        className="text-[var(--ink-3)] hover:text-[var(--ink)]"
                        title="Copy"
                      >
                        <Copy size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <button
                onClick={() => runJob(job)}
                disabled={isRunning}
                className="btn-money flex items-center gap-2 text-sm whitespace-nowrap"
                data-testid={`run-${job.id}`}
              >
                {isRunning ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />}
                Run now
              </button>
            </div>

            {last && (
              <div
                className={
                  "mt-4 p-3 rounded-md flex items-start gap-2 text-sm " +
                  (last.ok ? "bg-[var(--primary)]/8 border border-[var(--primary)]/30" : "bg-red-50 border border-red-200")
                }
              >
                {last.ok ? (
                  <CheckCircle2 size={14} className="text-[var(--primary-dark)] flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle size={14} className="text-red-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[var(--ink)] text-xs">
                    Last run {new Date(last.at).toLocaleString()}
                  </div>
                  {last.ok ? (
                    <div className="text-[var(--ink-2)] text-xs mt-1">
                      <strong className="text-[var(--primary-dark)]">{last.result?.sent ?? 0}</strong> sent ·{" "}
                      <strong>{last.result?.skipped ?? 0}</strong> skipped ·{" "}
                      <strong className={last.result?.errors > 0 ? "text-red-600" : ""}>
                        {last.result?.errors ?? 0}
                      </strong>{" "}
                      errors
                    </div>
                  ) : (
                    <div className="text-red-700 text-xs mt-1">{last.error}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Cron setup hint */}
      <div className="card p-5 bg-[var(--bg)]">
        <h3 className="font-display font-bold text-sm text-[var(--ink)] mb-2 flex items-center gap-2">
          <CalendarClock size={14} /> External cron setup
        </h3>
        <p className="text-[var(--ink-2)] text-xs leading-relaxed">
          These endpoints are admin-authenticated, designed to be hit by an external scheduler (Render, GitHub Actions,
          Cloudflare Cron Triggers, etc.). Pass a long-lived admin JWT in the <code className="px-1.5 py-0.5 bg-white border border-[var(--line)] rounded text-[var(--ink)] font-mono text-[10px]">Authorization</code> header.
          For testing or one-off backfills, use <em>Run now</em>.
        </p>
      </div>
    </div>
  );
}
